import React from 'react';
import T from 'prop-types';
import { ThemeProvider } from 'styled-components';
import { CheckOutlined } from '@ant-design/icons';

import CheckoutButtonStandard from '../checkoutButtonStandard';
import CheckoutButtonAdvanced from '../checkoutButtonAdvanced';
import CheckoutButtonProfessional from '../checkoutButtonProfessional';
import PlanIcon from '../planIcon';
import TierTag from '../tierTag';

import { rootDomainUrl } from '../../utils/url';

import {
  Container,
  PlanContainer,
  Plan,
  PlanHeader,
  PlanTitle,
  PlanDesc,
  PlanBody,
  Price,
  PriceNote,
  ListTitle,
  List,
  ListItem,
  ListItemLabel,
  ListItemValue,
  Link,
  UnavailableIcon,
} from './style';

const tier1Link = (
  <>
    <Link href={rootDomainUrl('/metrics#tier-1')} target="_blank">
      Tier 1 Metrics
    </Link>
    :
  </>
);

const tier2Link = (
  <>
    <Link href={rootDomainUrl('/metrics#tier-2')} target="_blank">
      Tier 2 Metrics
    </Link>
    :
  </>
);

const tier3Link = (
  <>
    <Link href={rootDomainUrl('/metrics#tier-3')} target="_blank">
      Tier 3 Metrics
    </Link>
    :
  </>
);

const Plans = ({ priceAdvanced, planAdvanced, priceProfessional, planProfessional }) => (
  <Container>
    <PlanContainer>
      <Plan>
        <PlanHeader>
          <PlanIcon type="standard" />
          <PlanTitle>Standard</PlanTitle>
          <PlanDesc>On-chain data for industry spectators and retail investors.</PlanDesc>

          <Price>$0</Price>
          <PriceNote>&nbsp;</PriceNote>

          <CheckoutButtonStandard />
        </PlanHeader>

        <PlanBody>
          <ListTitle>Resolution:</ListTitle>
          <List>
            <ListItem>
              <ListItemLabel>
                <TierTag dot tier={1} />
                {tier1Link}
              </ListItemLabel>
              <ListItemValue>24 hour</ListItemValue>
            </ListItem>
            <ListItem>
              <ListItemLabel>
                <TierTag dot tier={2} />
                {tier2Link}
              </ListItemLabel>
              <ListItemValue>24 hour</ListItemValue>
            </ListItem>
            <ListItem>
              <ListItemLabel>
                <TierTag dot tier={3} />
                {tier3Link}
              </ListItemLabel>
              <ListItemValue>
                <UnavailableIcon />
              </ListItemValue>
            </ListItem>
          </List>

          <ListTitle>Lag:</ListTitle>
          <List>
            <ListItem>
              <ListItemLabel>
                <TierTag dot tier={1} />
                {tier1Link}
              </ListItemLabel>
              <ListItemValue>No lag</ListItemValue>
            </ListItem>
            <ListItem>
              <ListItemLabel>
                <TierTag dot tier={2} />
                {tier2Link}
              </ListItemLabel>
              <ListItemValue>1 month lag</ListItemValue>
            </ListItem>
            <ListItem>
              <ListItemLabel>
                <TierTag dot tier={3} />
                {tier3Link}
              </ListItemLabel>
              <ListItemValue>
                <UnavailableIcon />
              </ListItemValue>
            </ListItem>
          </List>

          <ListTitle>Access:</ListTitle>
          <List>
            <ListItem>
              <ListItemLabel>Historic Data:</ListItemLabel>
              <ListItemValue>Full history</ListItemValue>
            </ListItem>
            <ListItem>
              <ListItemLabel>Data Formats:</ListItemLabel>
              <ListItemValue>JSON</ListItemValue>
            </ListItem>
            <ListItem>
              <ListItemLabel>API Limits:</ListItemLabel>
              <ListItemValue>30 req/min</ListItemValue>
            </ListItem>
          </List>

          <ListTitle>Reports:</ListTitle>
          <List>
            <ListItem>
              <ListItemLabel>The Week On-Chain</ListItemLabel>
              <ListItemValue>
                <CheckOutlined />
              </ListItemValue>
            </ListItem>
          </List>
        </PlanBody>
      </Plan>

      <ThemeProvider theme={{ isElevated: true }}>
        <Plan>
          <PlanHeader>
            <PlanIcon type="advanced" />
            <PlanTitle>Advanced</PlanTitle>
            <PlanDesc>On-chain data and reports for experienced traders and investors.</PlanDesc>

            <Price>{priceAdvanced}</Price>
            <PriceNote>per month</PriceNote>

            <CheckoutButtonAdvanced plan={planAdvanced} />
          </PlanHeader>

          <PlanBody>
            <ListTitle>Resolution:</ListTitle>
            <List>
              <ListItem>
                <ListItemLabel>
                  <TierTag dot tier={1} />
                  {tier1Link}
                </ListItemLabel>
                <ListItemValue>Up to 1 hour</ListItemValue>
              </ListItem>
              <ListItem>
                <ListItemLabel>
                  <TierTag dot tier={2} />
                  {tier2Link}
                </ListItemLabel>
                <ListItemValue>24 hour</ListItemValue>
              </ListItem>
              <ListItem>
                <ListItemLabel>
                  <TierTag dot tier={3} />
                  {tier3Link}
                </ListItemLabel>
                <ListItemValue>24 hour</ListItemValue>
              </ListItem>
            </List>

            <ListTitle>Lag:</ListTitle>
            <List>
              <ListItem>
                <ListItemLabel>
                  <TierTag dot tier={1} />
                  {tier1Link}
                </ListItemLabel>
                <ListItemValue>No lag</ListItemValue>
              </ListItem>
              <ListItem>
                <ListItemLabel>
                  <TierTag dot tier={2} />
                  {tier2Link}
                </ListItemLabel>
                <ListItemValue>No lag</ListItemValue>
              </ListItem>
              <ListItem>
                <ListItemLabel>
                  <TierTag dot tier={3} />
                  {tier3Link}
                </ListItemLabel>
                <ListItemValue>1 year lag</ListItemValue>
              </ListItem>
            </List>

            <ListTitle>Access:</ListTitle>
            <List>
              <ListItem>
                <ListItemLabel>Historic Data:</ListItemLabel>
                <ListItemValue>Full history</ListItemValue>
              </ListItem>
              <ListItem>
                <ListItemLabel>Data Formats:</ListItemLabel>
                <ListItemValue>JSON, CSV</ListItemValue>
              </ListItem>
              <ListItem>
                <ListItemLabel>API Limits:</ListItemLabel>
                <ListItemValue>120 req/min</ListItemValue>
              </ListItem>
            </List>

            <ListTitle>Reports:</ListTitle>
            <List>
              <ListItem>
                <ListItemLabel>The Week On-Chain</ListItemLabel>
                <ListItemValue>
                  <CheckOutlined />
                </ListItemValue>
              </ListItem>
            </List>
          </PlanBody>
        </Plan>
      </ThemeProvider>

      <Plan>
        <PlanHeader>
          <PlanIcon type="professional" />
          <PlanTitle>Professional</PlanTitle>
          <PlanDesc>On-chain data and reports for experienced traders and investors.</PlanDesc>

          <Price>{priceProfessional}</Price>
          <PriceNote>per month</PriceNote>

          <CheckoutButtonProfessional plan={planProfessional} />
        </PlanHeader>

        <PlanBody>
          <ListTitle>Resolution:</ListTitle>
          <List>
            <ListItem>
              <ListItemLabel>
                <TierTag dot tier={1} />
                {tier1Link}
              </ListItemLabel>
              <ListItemValue>Up to 10 minutes</ListItemValue>
            </ListItem>
            <ListItem>
              <ListItemLabel>
                <TierTag dot tier={2} />
                {tier2Link}
              </ListItemLabel>
              <ListItemValue>Up to 10 minutes</ListItemValue>
            </ListItem>
            <ListItem>
              <ListItemLabel>
                <TierTag dot tier={3} />
                {tier3Link}
              </ListItemLabel>
              <ListItemValue>Up to 10 minutes</ListItemValue>
            </ListItem>
          </List>

          <ListTitle>Lag:</ListTitle>
          <List>
            <ListItem>
              <ListItemLabel>
                <TierTag dot tier={1} />
                {tier1Link}
              </ListItemLabel>
              <ListItemValue>No lag</ListItemValue>
            </ListItem>
            <ListItem>
              <ListItemLabel>
                <TierTag dot tier={2} />
                {tier2Link}
              </ListItemLabel>
              <ListItemValue>No lag</ListItemValue>
            </ListItem>
            <ListItem>
              <ListItemLabel>
                <TierTag dot tier={3} />
                {tier3Link}
              </ListItemLabel>
              <ListItemValue>No lag</ListItemValue>
            </ListItem>
          </List>

          <ListTitle>Access:</ListTitle>
          <List>
            <ListItem>
              <ListItemLabel>Historic Data:</ListItemLabel>
              <ListItemValue>Full history</ListItemValue>
            </ListItem>
            <ListItem>
              <ListItemLabel>Data Formats:</ListItemLabel>
              <ListItemValue>JSON, CSV</ListItemValue>
            </ListItem>
            <ListItem>
              <ListItemLabel>API Limits:</ListItemLabel>
              <ListItemValue>600 req/min</ListItemValue>
            </ListItem>
          </List>

          <ListTitle>Reports:</ListTitle>
          <List>
            <ListItem>
              <ListItemLabel>The Week On-Chain</ListItemLabel>
              <ListItemValue>
                <CheckOutlined />
              </ListItemValue>
            </ListItem>
          </List>
        </PlanBody>
      </Plan>
    </PlanContainer>
  </Container>
);

Plans.propTypes = {
  priceAdvanced: T.string.isRequired,
  planAdvanced: T.string.isRequired,
  priceProfessional: T.string.isRequired,
  planProfessional: T.string.isRequired,
};

export default Plans;
